<template>
  <div class="recent-quotes">
    <!-- Skeleton -->
    <div v-if="loading" class="rq-skeleton">
      <div v-for="n in 5" :key="n" class="rq-skeleton-row">
        <div class="skeleton-line w60"></div>
        <div class="skeleton-line w40"></div>
      </div>
    </div>

    <!-- List -->
    <div v-else-if="quotes.length" class="rq-list">
      <div
        v-for="quote in quotes"
        :key="quote.id"
        class="rq-item"
        @click="onQuoteClick(quote)"
      >
        <div class="rq-item-left">
          <span class="rq-code">{{ quote.code }}</span>
          <span class="rq-client">{{ quote.client_name }}</span>
        </div>
        <div class="rq-item-right">
          <span class="rq-time">{{ timeAgo(quote.created_at) }}</span>
          <span class="rq-total">${{ formatMoney(quote.total) }}</span>
          <span :class="['rq-tag', statusClass(quote.smart_status)]">
            {{ statusLabel(quote.smart_status) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="rq-empty">
      <i class="el-icon-document"></i>
      <span>Sin cotizaciones recientes</span>
    </div>
  </div>
</template>

<script>
import dashboardService from "../services/dashboardService";

const STATUS_MAP = {
  active: { label: "Activa", cls: "tag-teal-light" },
  no_response: { label: "Sin respuesta", cls: "tag-amber-light" },
  expired: { label: "Expirada", cls: "tag-red-light" },
  approved: { label: "Aprobada", cls: "tag-purple-light" },
  invoiced: { label: "Facturada", cls: "tag-green" },
  rejected: { label: "Rechazada", cls: "tag-red" }
};

export default {
  name: "RecentQuotes",
  props: {
    period: { type: String, default: "month" }
  },
  emits: ["open-quote"],
  data() {
    return {
      loading: true,
      quotes: []
    };
  },
  watch: {
    period: {
      immediate: true,
      handler() {
        this.fetchQuotes();
      }
    }
  },
  methods: {
    async fetchQuotes() {
      this.loading = true;
      try {
        const res = await dashboardService.getRecentQuotes({
          params: { limit: 5 }
        });
        if (res.data && res.data.success) {
          this.quotes = res.data.data.quotes || res.data.data || [];
        }
      } catch (e) {
        // Mantener valores actuales
      } finally {
        this.loading = false;
      }
    },
    statusClass(status) {
      return (STATUS_MAP[status] || STATUS_MAP.active).cls;
    },
    statusLabel(status) {
      return (STATUS_MAP[status] || { label: status }).label;
    },
    timeAgo(date) {
      if (!date) return "";
      const now = new Date();
      const d = new Date(date);
      const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (diff === 0) return "hoy";
      if (diff === 1) return "hace 1 dia";
      return `hace ${diff} dias`;
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    },
    onQuoteClick(quote) {
      this.$emit("open-quote", quote);
      if (quote.version_id) {
        this.$router.push({
          name: "sales.quotes.view",
          params: { id: quote.id, idVersion: quote.version_id }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.recent-quotes {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 18px;
}
.rq-list {
  display: flex;
  flex-direction: column;
}
.rq-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid $border;
  cursor: pointer;
  transition: background 0.15s;
  margin: 0 -18px;
  padding-left: 18px;
  padding-right: 18px;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
}
.rq-item-left {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rq-code {
  font-size: 13px;
  font-weight: 500;
  color: $primary;
}
.rq-client {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.rq-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.rq-time {
  font-size: 12px;
  color: #c0c4cc;
}
.rq-total {
  font-size: 13px;
  font-weight: 500;
  color: $primary;
}
.rq-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.tag-teal-light {
  background: #e4fff4;
  color: #1d9e75;
}
.tag-amber-light {
  background: #fff8e6;
  color: #ba7517;
}
.tag-red-light {
  background: #fff0f0;
  color: #e24b4a;
}
.tag-purple-light {
  background: #ece8ff;
  color: #534ab7;
}
.tag-green {
  background: #1d9e75;
  color: #fff;
}
.tag-red {
  background: #e24b4a;
  color: #fff;
}

.rq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;
  gap: 8px;
  i {
    font-size: 28px;
  }
  span {
    font-size: 12px;
  }
}

// Skeleton
.rq-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.rq-skeleton-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}
.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w40 {
    width: 40%;
  }
  &.w60 {
    width: 60%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 600px) {
  .rq-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .rq-item-right {
    width: 100%;
    justify-content: flex-start;
  }
  .rq-client {
    max-width: 100%;
  }
}
</style>
